<template>
  <div class="bp-page">
    <!-- Hero header -->
    <div class="bp-hero">
      <div class="bp-hero__text">
        <div class="bp-hero__badge">Gantt chart</div>
        <h2 class="bp-hero__title">Byudjet jarayoni</h2>
        <p class="bp-hero__sub">
          Portalda ko'rsatiladigan byudjet jarayoni chizmasini — guruhlar va bandlarni boshqarish
        </p>
      </div>
      <div class="bp-hero__stats">
        <div class="bp-stat">
          <span class="bp-stat__value">{{ groups.length }}</span>
          <span class="bp-stat__label">Guruh</span>
        </div>
        <div class="bp-stat__divider"/>
        <div class="bp-stat">
          <span class="bp-stat__value">{{ items.length }}</span>
          <span class="bp-stat__label">Band</span>
        </div>
        <div class="bp-stat__divider"/>
        <div class="bp-stat">
          <span class="bp-stat__value">{{ activeGroupsCount }}</span>
          <span class="bp-stat__label">Faol guruh</span>
        </div>
      </div>
    </div>

    <el-tabs v-model="activeTab" class="bp-tabs">
      <!-- ===================== PREVIEW ===================== -->
      <el-tab-pane name="preview">
        <template #label>
          <span class="bp-tab-label">
            <svg class="bp-tab-ico" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="2.5" width="12" height="11" rx="2"/>
              <path d="M2 6.5h12M6.5 6.5v7"/>
            </svg>
            Ko'rinish
          </span>
        </template>
        <el-skeleton v-if="groupsLoading || itemsLoading" :rows="6" animated/>
        <BudgetProcessPreview
            v-else
            :groups="groups"
            :items="items"
            @create-group="openCreateGroupFromPreview"
        />
      </el-tab-pane>

      <!-- ===================== GROUPS ===================== -->
      <el-tab-pane name="groups">
        <template #label>
          <span class="bp-tab-label">
            <svg class="bp-tab-ico" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M8 2l6 3-6 3-6-3z"/>
              <path d="M2 8l6 3 6-3"/>
              <path d="M2 11l6 3 6-3"/>
            </svg>
            Guruhlar
            <span v-if="groups.length" class="bp-tab-count">{{ groups.length }}</span>
          </span>
        </template>

        <div class="bp-toolbar">
          <p class="bp-toolbar__hint">Y o'qidagi qatorlar — har biri o'z rangi va bandlariga ega</p>
          <el-button type="primary" @click="openCreateGroup">
            <span class="bp-btn-plus">+</span> Guruh qo'shish
          </el-button>
        </div>

        <el-skeleton v-if="groupsLoading" :rows="6" animated/>
        <MfEmpty
            v-else-if="!groups.length"
            title="Guruhlar topilmadi"
            description="Yangi guruh yaratish uchun yuqoridagi tugmani bosing"
        />
        <div v-else class="bp-table-wrap">
          <el-table :data="groups" class="bp-table" :row-class-name="groupRowClass">
            <el-table-column label="Guruh" min-width="260">
              <template #default="scope">
                <div class="bp-grp-cell">
                  <span class="bp-grp-cell__bar" :style="{background: scope.row.color || '#64748B'}"/>
                  <div class="bp-grp-cell__text">
                    <span class="bp-grp-cell__title">{{ displayLangText(scope.row.title) || "..." }}</span>
                    <span class="bp-grp-cell__hex">{{ scope.row.color || "#64748B" }}</span>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Tartib" width="100" align="center">
              <template #default="scope">
                <span class="bp-order-chip" :style="orderChipStyle(scope.row.color)">{{ scope.row.order_number }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Bandlar" width="120" align="center">
              <template #default="scope">
                <el-tag
                    round
                    effect="light"
                    :type="groupItemCounts[scope.row.id] ? 'primary' : 'info'"
                >
                  {{ groupItemCounts[scope.row.id] || 0 }} ta
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="Holati" width="120" align="center">
              <template #default="scope">
                <el-switch
                    :model-value="scope.row.is_active !== false"
                    :loading="togglingGroupId === scope.row.id"
                    style="--el-switch-on-color: #16A34A"
                    @change="onToggleGroupActive(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column fixed="right" label="Amallar" width="150" align="center">
              <template #default="scope">
                <el-space :size="8">
                  <el-button type="primary" size="small" title="Tahrirlash" @click="openEditGroup(scope.row)">
                    <img height="15" src="@/assets/images/icons/edit.svg" alt="Tahrirlash"/>
                  </el-button>
                  <el-button
                      type="danger"
                      size="small"
                      title="O'chirish"
                      :loading="deletingGroupId === scope.row.id"
                      @click="onDeleteGroup(scope.row)"
                  >
                    <img height="15" src="@/assets/images/icons/delete.svg" alt="O'chirish"/>
                  </el-button>
                </el-space>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- ===================== ITEMS ===================== -->
      <el-tab-pane name="items">
        <template #label>
          <span class="bp-tab-label">
            <svg class="bp-tab-ico" viewBox="0 0 16 16" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="2" y="3" width="9" height="3.2" rx="1.6"/>
              <rect x="5" y="9.8" width="9" height="3.2" rx="1.6"/>
            </svg>
            Bandlar
            <span v-if="items.length" class="bp-tab-count">{{ items.length }}</span>
          </span>
        </template>

        <div class="bp-toolbar">
          <el-select
              v-model="itemsGroupFilter"
              placeholder="Barcha guruhlar"
              clearable
              class="bp-filter"
          >
            <el-option
                v-for="g in groups"
                :key="g.id"
                :label="displayLangText(g.title) || '...'"
                :value="g.id"
            >
              <span class="bp-opt">
                <span class="bp-opt__swatch" :style="{background: g.color || '#64748B'}"/>
                {{ displayLangText(g.title) || "..." }}
              </span>
            </el-option>
          </el-select>
          <el-button type="primary" :disabled="!groups.length" @click="goCreateItem">
            <span class="bp-btn-plus">+</span> Band qo'shish
          </el-button>
        </div>

        <el-skeleton v-if="itemsLoading" :rows="6" animated/>
        <MfEmpty
            v-else-if="!filteredItems.length"
            title="Bandlar topilmadi"
            :description="itemsGroupFilter ? 'Bu guruhda band yo\'q' : 'Yangi band yaratish uchun yuqoridagi tugmani bosing'"
        />
        <div v-else class="bp-table-wrap">
          <el-table :data="filteredItems" class="bp-table" :row-class-name="itemRowClass">
            <el-table-column label="Nomi" min-width="260">
              <template #default="scope">
                <span class="bp-item-title">{{ displayLangText(scope.row.title) || "..." }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Guruh" min-width="170">
              <template #default="scope">
                <span class="bp-grp-tag">
                  <span class="bp-grp-tag__dot" :style="{background: groupColorById(scope.row.group_id)}"/>
                  {{ groupTitleById(scope.row.group_id) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Davr" min-width="210">
              <template #default="scope">
                <span class="bp-period-pill">
                  {{ periodLabel(scope.row.start_year, scope.row.start_month) }}
                  <span class="bp-period-pill__arrow">→</span>
                  {{ periodLabel(scope.row.end_year, scope.row.end_month) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="Tartib" width="90" align="center">
              <template #default="scope">
                <span class="bp-order-chip" :style="orderChipStyle(groupColorById(scope.row.group_id))">{{ scope.row.order_number }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Holati" width="110" align="center">
              <template #default="scope">
                <el-switch
                    :model-value="scope.row.is_active !== false"
                    :loading="togglingItemId === scope.row.id"
                    style="--el-switch-on-color: #16A34A"
                    @change="onToggleItemActive(scope.row)"
                />
              </template>
            </el-table-column>
            <el-table-column fixed="right" label="Amallar" width="150" align="center">
              <template #default="scope">
                <el-space :size="8">
                  <el-button type="primary" size="small" title="Tahrirlash" @click="goEditItem(scope.row)">
                    <img height="15" src="@/assets/images/icons/edit.svg" alt="Tahrirlash"/>
                  </el-button>
                  <el-button
                      type="danger"
                      size="small"
                      title="O'chirish"
                      :loading="deletingItemId === scope.row.id"
                      @click="onDeleteItem(scope.row)"
                  >
                    <img height="15" src="@/assets/images/icons/delete.svg" alt="O'chirish"/>
                  </el-button>
                </el-space>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>
    </el-tabs>

    <BudgetProcessGroupDialog
        v-model="groupDialogVisible"
        :group="editingGroup"
        :groups-count="groups.length"
        @saved="onGroupSaved"
    />
  </div>
</template>

<script>
import {ElMessage, ElMessageBox, ElNotification} from "element-plus";
import MfEmpty from "@/components/core/MfEmpty.vue";
import BudgetProcessPreview from "./BudgetProcessPreview.vue";
import BudgetProcessGroupDialog from "./BudgetProcessGroupDialog.vue";
import {API_BASE, displayLangText, periodLabel} from "./budgetProcessTypes";

export default {
  name: "BudgetProcessList",
  components: {MfEmpty, BudgetProcessPreview, BudgetProcessGroupDialog},
  data() {
    return {
      activeTab: "preview",
      groups: [],
      items: [],
      groupsLoading: false,
      itemsLoading: false,
      groupDialogVisible: false,
      editingGroup: null,
      deletingGroupId: null,
      togglingGroupId: null,
      deletingItemId: null,
      togglingItemId: null,
      itemsGroupFilter: "",
    };
  },
  computed: {
    activeGroupsCount() {
      return this.groups.filter((g) => g.is_active !== false).length;
    },
    groupItemCounts() {
      const counts = {};
      this.items.forEach((item) => {
        counts[item.group_id] = (counts[item.group_id] || 0) + 1;
      });
      return counts;
    },
    filteredItems() {
      if (!this.itemsGroupFilter) return this.items;
      return this.items.filter((item) => item.group_id === this.itemsGroupFilter);
    },
  },
  methods: {
    displayLangText,
    periodLabel,
    groupRowClass({row}) {
      return row.is_active === false ? "bp-row--inactive" : "";
    },
    itemRowClass({row}) {
      return row.is_active === false ? "bp-row--inactive" : "";
    },
    handleApiError(error, fallbackTitle) {
      const data = error?.response?.data;
      ElNotification({
        title: fallbackTitle,
        message: data?.message || "Iltimos, qayta urinib ko'ring.",
        type: "error",
      });
    },
    fetchGroups() {
      this.groupsLoading = true;
      this.$http
          .get(`${API_BASE}/groups`)
          .then(({data}) => {
            this.groups = Array.isArray(data) ? data : [];
          })
          .catch((error) => this.handleApiError(error, "Guruhlarni yuklashda xatolik"))
          .finally(() => {
            this.groupsLoading = false;
          });
    },
    // Always fetches the FULL item list (no group_id param): the preview and
    // the group item-counts both need every item regardless of the Items-tab
    // filter, which is applied client-side via `filteredItems` instead.
    fetchItems() {
      this.itemsLoading = true;
      this.$http
          .get(`${API_BASE}/items`)
          .then(({data}) => {
            this.items = Array.isArray(data) ? data : [];
          })
          .catch((error) => this.handleApiError(error, "Bandlarni yuklashda xatolik"))
          .finally(() => {
            this.itemsLoading = false;
          });
    },
    groupById(id) {
      return this.groups.find((g) => g.id === id);
    },
    groupTitleById(id) {
      const group = this.groupById(id);
      return group ? displayLangText(group.title) || "..." : "—";
    },
    groupColorById(id) {
      const group = this.groupById(id);
      return group?.color || "#64748B";
    },
    orderChipStyle(color) {
      const hex = color || "#64748B";
      const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
      if (!m) return {};
      const int = parseInt(m[1], 16);
      let r = (int >> 16) & 255;
      let g = (int >> 8) & 255;
      let b = int & 255;
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      const ceil = 150;
      if (lum > ceil) {
        const k = ceil / lum;
        r = Math.round(r * k);
        g = Math.round(g * k);
        b = Math.round(b * k);
      }
      return {
        color: "#ffffff",
        background: `rgb(${r}, ${g}, ${b})`,
      };
    },
    openCreateGroup() {
      this.editingGroup = null;
      this.groupDialogVisible = true;
    },
    openEditGroup(group) {
      this.editingGroup = group;
      this.groupDialogVisible = true;
    },
    openCreateGroupFromPreview() {
      this.activeTab = "groups";
      this.openCreateGroup();
    },
    onGroupSaved() {
      this.fetchGroups();
      this.fetchItems();
    },
    onToggleGroupActive(group) {
      this.togglingGroupId = group.id;
      this.$http
          .put(`${API_BASE}/groups/${group.id}`, {
            title: group.title,
            color: group.color,
            order_number: group.order_number,
            is_active: !(group.is_active !== false),
          })
          .then(() => {
            ElMessage({type: "success", message: "Holati yangilandi"});
          })
          .catch((error) => this.handleApiError(error, "Holatni o'zgartirishda xatolik"))
          .finally(() => {
            this.togglingGroupId = null;
            this.fetchGroups();
          });
    },
    onDeleteGroup(group) {
      const count = this.groupItemCounts[group.id] || 0;
      const message = count > 0
          ? `Bu guruhni o'chirish unga tegishli ${count} ta bandni ham butunlay o'chiradi. Davom etasizmi?`
          : "Guruhni o'chirasizmi?";
      ElMessageBox.confirm(message, "Guruhni o'chirish", {
        confirmButtonText: "Ha, o'chirish",
        cancelButtonText: "Bekor qilish",
        type: "warning",
      })
          .then(() => {
            this.deletingGroupId = group.id;
            this.$http
                .delete(`${API_BASE}/groups/${group.id}`)
                .then(() => {
                  ElMessage({type: "success", message: "Guruh o'chirildi"});
                  this.fetchGroups();
                  this.fetchItems();
                })
                .catch((error) => this.handleApiError(error, "O'chirishda xatolik"))
                .finally(() => {
                  this.deletingGroupId = null;
                });
          })
          .catch(() => {
          });
    },
    goCreateItem() {
      const query = this.itemsGroupFilter ? {group_id: this.itemsGroupFilter} : {};
      this.$router.push({name: "BudgetProcessItemAdd", query});
    },
    goEditItem(item) {
      this.$router.push({name: "BudgetProcessItemEdit", params: {id: item.id}});
    },
    onToggleItemActive(item) {
      // List rows omit `content`; fetch the full item first so the follow-up
      // full-replacement PUT doesn't wipe the item's markdown content.
      this.togglingItemId = item.id;
      this.$http
          .get(`${API_BASE}/items/${item.id}`)
          .then(({data}) => {
            const payload = {
              group_id: data.group_id,
              title: data.title,
              content: data.content,
              start_year: data.start_year,
              start_month: data.start_month,
              end_year: data.end_year,
              end_month: data.end_month,
              order_number: data.order_number,
              is_active: !(data.is_active !== false),
            };
            return this.$http.put(`${API_BASE}/items/${item.id}`, payload);
          })
          .then(() => {
            ElMessage({type: "success", message: "Holati yangilandi"});
          })
          .catch((error) => this.handleApiError(error, "Holatni o'zgartirishda xatolik"))
          .finally(() => {
            this.togglingItemId = null;
            this.fetchItems();
          });
    },
    onDeleteItem(item) {
      ElMessageBox.confirm("Bandni o'chirasizmi?", "Bandni o'chirish", {
        confirmButtonText: "Ha, o'chirish",
        cancelButtonText: "Bekor qilish",
        type: "warning",
      })
          .then(() => {
            this.deletingItemId = item.id;
            this.$http
                .delete(`${API_BASE}/items/${item.id}`)
                .then(() => {
                  ElMessage({type: "success", message: "Band o'chirildi"});
                  this.fetchItems();
                })
                .catch((error) => this.handleApiError(error, "O'chirishda xatolik"))
                .finally(() => {
                  this.deletingItemId = null;
                });
          })
          .catch(() => {
          });
    },
  },
  created() {
    if (["preview", "groups", "items"].includes(this.$route.query.tab)) {
      this.activeTab = this.$route.query.tab;
    }
  },
  mounted() {
    this.fetchGroups();
    this.fetchItems();
  },
};
</script>

<style scoped lang="scss">
$brand: #007791;
$brand-dark: #005c72;
$navy: #0a194e;
$border: #ebeef5;
$muted: #8a94a6;

.bp-page {
  padding-bottom: 8px;
}

/* ---------- hero ---------- */
.bp-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  padding: 22px 26px;
  margin-bottom: 20px;
  border-radius: 16px;
  color: #fff;
  background:
      radial-gradient(120% 140% at 100% 0%, rgba(255, 255, 255, 0.18), transparent 55%),
      linear-gradient(135deg, #007791 0%, #0a5f77 55%, #0a194e 130%);
  box-shadow: 0 12px 30px rgba(0, 92, 114, 0.25);

  &__badge {
    display: inline-block;
    padding: 3px 10px;
    margin-bottom: 8px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    background: rgba(255, 255, 255, 0.16);
    border-radius: 20px;
  }

  &__title {
    margin: 0;
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 24px;
  }

  &__sub {
    margin: 6px 0 0;
    font-size: 13px;
    max-width: 520px;
    opacity: 0.85;
  }

  &__stats {
    display: flex;
    align-items: center;
    gap: 18px;
    padding: 6px 4px;
  }
}

.bp-stat {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 62px;

  &__value {
    font-family: "Montserrat-Bold", sans-serif;
    font-size: 28px;
    line-height: 1;
  }

  &__label {
    margin-top: 5px;
    font-size: 11px;
    opacity: 0.8;
  }

  &__divider {
    width: 1px;
    height: 34px;
    background: rgba(255, 255, 255, 0.25);
  }
}

/* ---------- tabs ---------- */
.bp-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 18px;
  }

  :deep(.el-tabs__item) {
    font-family: "Montserrat-Medium", sans-serif;
    font-size: 14px;
    height: 44px;
  }

  :deep(.el-tabs__item.is-active) {
    color: $brand;
  }

  :deep(.el-tabs__active-bar) {
    background-color: $brand;
    height: 3px;
    border-radius: 3px;
  }
}

.bp-tab-label {
  display: inline-flex;
  align-items: center;
  gap: 7px;
}

.bp-tab-ico {
  display: inline-flex;
  flex-shrink: 0;
  opacity: 0.85;
}

.bp-tab-count {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 20px;
  background: rgba(0, 119, 145, 0.12);
  color: $brand;
  font-size: 12px;
  font-weight: 600;
}

/* ---------- toolbar ---------- */
.bp-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;

  &__hint {
    margin: 0;
    font-size: 13px;
    color: $muted;
  }
}

.bp-filter {
  width: 260px;
}

.bp-btn-plus {
  font-size: 16px;
  margin-right: 2px;
}

.bp-opt {
  display: inline-flex;
  align-items: center;
  gap: 8px;

  &__swatch {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    flex-shrink: 0;
  }
}

/* ---------- table ---------- */
.bp-table-wrap {
  border: 1px solid $border;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 14px rgba(15, 30, 80, 0.04);
}

.bp-table {
  width: 100%;

  // Keep the fixed "Amallar" column above the scrolling cells so the Holati
  // switch can't bleed over it: EP's fixed cells default to z-index 2 with an
  // inherited (often see-through) background — force an opaque bg + higher z.
  :deep(.el-table-fixed-column--right) {
    background-color: #fff !important;
    z-index: 3 !important;
  }

  // header's fixed cell keeps the header tint, not white
  :deep(th.el-table-fixed-column--right) {
    background-color: #f6f9fc !important;
  }

  :deep(th.el-table__cell) {
    background: #f6f9fc;
    color: $navy;
    font-family: "Montserrat-Medium", sans-serif;
    font-weight: 600;
    height: 50px;
  }

  :deep(.el-table__row) {
    transition: background 0.12s ease;
  }

  // Opaque (never rgba): a translucent hover tint on the sticky "Amallar"
  // column would let the scrolling Holati switch show through it.
  // #f4f9fa == rgba(0,119,145,.04) flattened onto white.
  :deep(.el-table__row:hover > td.el-table__cell) {
    background-color: #f4f9fa !important;
  }

  :deep(.bp-row--inactive) {
    opacity: 0.55;
  }
}

.bp-grp-cell {
  display: flex;
  align-items: center;
  gap: 12px;

  &__bar {
    width: 6px;
    height: 34px;
    border-radius: 4px;
    flex-shrink: 0;
  }

  &__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__title {
    font-weight: 600;
    color: #303133;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__hex {
    font-size: 11px;
    color: $muted;
    font-family: "SFMono-Regular", Consolas, monospace;
  }
}

.bp-item-title {
  font-weight: 600;
  color: #303133;
}

.bp-grp-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  background: #f4f6fa;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #475569;

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
}

.bp-period-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: rgba(0, 119, 145, 0.07);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: $brand-dark;
  white-space: nowrap;

  &__arrow {
    color: $muted;
  }
}

.bp-order-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 26px;
  height: 24px;
  padding: 0 7px;
  border-radius: 7px;
  background: #eef1f6;
  color: #475569;
  font-size: 12px;
  font-weight: 600;
}

</style>
